import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import formOptions from "../../config/formOptions.json";
import Header from "../../components/Header";
import MarkdownEditor from "../../components/MarkdownEditor";
import LabelSelector from "../../components/LabelSelector";
import DateInput from "../../components/DateInput";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import { graphqlEndpoint } from "../../config";
import { parseUKDate } from "../../utils/dateUtiles";

import { CREATE_PROJECT } from "../../graph/projects/mutations";
import { READ_PROJECTS_FILTER } from "../../graph/projects/queries";

const client = new GraphQLClient(graphqlEndpoint);

const allowedLabels =
  (formOptions?.labelOptions || []).map((l) => l.value) || [];

const projectSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().nullable(),
  isSOPTemplate: yup.boolean(),
  labels: yup.array().of(yup.string().oneOf(allowedLabels, "Invalid label")),
  sourceProjectId: yup.string().nullable(),
  dueDate: yup.string().nullable(),
  status: yup.string().required("Status is required"),
});

const CreateProjectForm = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const [sourceProjects, setSourceProjects] = useState([]);

  const sopValue = state?.sop ?? false;
  const name = state?.name ?? (sopValue ? "SOP" : "Project");
  const redirectPath = state?.redirectPath ?? (sopValue ? "/manageSOPs" : "/manageProjects");

  const initialProjectValues = {
    title: "",
    description: "",
    isSOPTemplate: sopValue,
    labels: [],
    sourceProjectId: "",
    dueDate: "",
    status: "ACTIVE",
  };

  useEffect(() => {
    const fetchSourceProjects = async () => {
      if (!tenantId) return;

      try {
        const data = await client.request(READ_PROJECTS_FILTER, {
          filter: {
            tenantId,
            isSOPTemplate: true,
          },
        });

        setSourceProjects(data.readProjectsFilter || []);
      } catch (err) {
        console.error("Failed to load source projects:", err);
      }
    };

    fetchSourceProjects();
  }, [tenantId]);

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select a tenant before creating a project",
      });
      return;
    }

    try {
      const input = {
        title: values.title.trim(),
        description: values.description?.trim() || null,
        isSOPTemplate: values.isSOPTemplate,
        labels: values.labels || [],
        tenantId,
        sourceProjectId: values.sourceProjectId || null,
        dueDate: values.dueDate ? parseUKDate(values.dueDate) : null,
        status: values.status,
      };

      await client.request(CREATE_PROJECT, { input });

      setSnackbar({
        open: true,
        message: `${name} created successfully`,
        severity: "success",
      });

      resetForm();
      navigate(redirectPath);
    } catch (err) {
      const message = err?.response?.errors?.[0]?.message || "Unexpected error";
      console.error("Error creating project:", err);
      setSnackbar({
        open: true,
        message: `Failed to create ${name}: ${message}`,
        severity: "error",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title={`CREATE ${name.toUpperCase()}`}
          subtitle={`Select a tenant before creating a ${name.toLowerCase()}`}
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title={`CREATE ${name.toUpperCase()}`}
        subtitle={`Create a new ${name.toLowerCase()} for ${tenantName || "selected tenant"}`}
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialProjectValues}
        validationSchema={projectSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{ "& > div": { gridColumn: isNonMobile ? undefined : "span 4" } }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {(formOptions.projectStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateInput
                label="Due Date"
                name="dueDate"
                value={values.dueDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.dueDate}
                touched={touched.dueDate}
              />

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Source SOP</InputLabel>
                <Select
                  name="sourceProjectId"
                  value={values.sourceProjectId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sourceProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LabelSelector
                selectedLabels={values.labels}
                setFieldValue={setFieldValue}
                error={errors.labels}
                touched={touched.labels}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button type="submit" color="secondary" variant="contained">
                Create {name}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateProjectForm;